import { initializeApp } from 'firebase/app';
import { getAuth } from 'firebase/auth';
import { 
  initializeFirestore, 
  persistentLocalCache, 
  persistentMultipleTabManager,
  doc, 
  getDocFromServer, 
  onSnapshotsInSync 
} from 'firebase/firestore';
import { getStorage } from 'firebase/storage';
import firebaseConfig from '../../firebase-applet-config.json';

// Ensure we use the correct property name for the database ID
const dbId = (firebaseConfig as any).firestoreDatabaseId || (firebaseConfig as any).databaseId || '(default)';

const app = initializeApp(firebaseConfig);

// Initialize Firestore with persistent cache for better offline support
export const db = initializeFirestore(app, {
  localCache: persistentLocalCache({ tabManager: persistentMultipleTabManager() }),
  experimentalForceLongPolling: true
}, dbId);

export const auth = getAuth(app);
export const storage = getStorage(app);

// Set short retry times to fail fast
// This prevents long hangs when Storage connectivity is poor
storage.maxUploadRetryTime = 3000; // 3 seconds
storage.maxOperationRetryTime = 3000; // 3 seconds

// Connection state tracking
let isFirestoreConnected = true;
const connectionListeners: ((connected: boolean) => void)[] = [];

export const onConnectionChange = (callback: (connected: boolean) => void) => {
  connectionListeners.push(callback);
  callback(isFirestoreConnected);
  return () => {
    const index = connectionListeners.indexOf(callback);
    if (index > -1) connectionListeners.splice(index, 1);
  };
};

const notifyListeners = (connected: boolean) => {
  if (isFirestoreConnected === connected) return;
  isFirestoreConnected = connected;
  connectionListeners.forEach(cb => cb(connected));
};

// Validate Connection to Firestore
async function testConnection() {
  try {
    // Attempt to fetch a non-existent doc to test connection
    // We use getDocFromServer to bypass local cache and force a network check
    await getDocFromServer(doc(db, '_connection_test_', 'test'));
    if (!isFirestoreConnected) {
      console.log("Firestore connection restored.");
    }
    notifyListeners(true);
  } catch (error: any) {
    // "Failed to get document because the client is offline" is a common error during initial load or network issues
    const errorMessage = error?.message || String(error);
    if (errorMessage.includes('the client is offline') || errorMessage.includes('unavailable')) {
      // Only log as error if we were previously connected or if it persists
      console.warn("Firestore is currently offline or unavailable. Checking connection...");
      notifyListeners(false);
    } else if (errorMessage.includes('permission-denied')) {
      // Permission denied still means we are connected to the server
      console.log("Firestore connection verified (Permission Denied is expected for test doc).");
      notifyListeners(true);
    } else {
      console.error("Firestore connection test failed:", error);
      notifyListeners(false);
    }
  }
}

// Monitor snapshots in sync to detect connection issues
onSnapshotsInSync(db, () => {
  // If snapshots are in sync, it's a good sign we are connected
  if (!isFirestoreConnected) {
    testConnection();
  }
});

// Initial test
testConnection();

// Re-test periodically but less frequently to avoid noise
setInterval(testConnection, 60000);
